import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './components/ThemeProvider';

// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Forbidden from './pages/Forbidden';
import DocTypeIndex from './pages/DocTypeIndex';
import DocViewer from './pages/DocViewer';
import CollectionPdfExport from './pages/CollectionPdfExport';
import AdminAccess from './pages/AdminAccess';
import SearchResults from './pages/SearchResults';

// Components
import FloatingSearchButton from './components/FloatingSearchButton';

const queryClient = new QueryClient();

function AppRoutes() {
  const { user, loading } = useAuth();

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }
    
    if (!user) {
      return <Navigate to="/auth/login" replace />;
    }
    
    return <>{children}</>;
  };

  const AuthRedirect = () => {
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }
    
    return <Navigate to={user ? "/dashboard" : "/auth/login"} replace />;
  };

  return (
    <Routes>
      {/* Root redirect based on auth state */}
      <Route path="/" element={<AuthRedirect />} />
      
      {/* Public routes */}
      <Route path="/auth/login" element={<Login />} />
      <Route path="/auth/signup" element={<Signup />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/forbidden" element={<Forbidden />} />
      
      {/* Search results page */}
      <Route path="/search" element={<SearchResults />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      {/* Admin route (before dynamic :slug to avoid conflict) */}
      <Route path="/admin/:org/:collection" element={
        <ProtectedRoute>
          <AdminAccess />
        </ProtectedRoute>
      } />

      {/* Collection routes — public or protected depending on is_public */}
      <Route path="/:org/:collection" element={<DocTypeIndex />} />
      <Route path="/:org/:collection/pdf" element={<CollectionPdfExport />} />
      <Route path="/:org/:collection/:slug" element={<DocViewer />} />
      
      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/auth/login" replace />} />
    </Routes>
  );
}

export default function App() {
  console.log('BASE_URL:', import.meta.env.BASE_URL);
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={base}>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
            <FloatingSearchButton />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
